
/**
 * 
 * Canvas Free drawing.
 */
import FloodFill from 'q-floodfill';
import { differenceCiede2000 } from 'd3-color-difference'

function rgba2hex({ r, g, b }) {
    r = r.toString(16)
    g = g.toString(16)
    b = b.toString(16)

    if (r.length == 1) r = '0' + r
    if (g.length == 1) g = '0' + g
    if (b.length == 1) b = '0' + b

    return '#' + r + g + b
}

function isSameColor(a: ColorRGBA, b: ColorRGBA, tolerance = 0): boolean {
    return differenceCiede2000(rgba2hex(a), rgba2hex(b)) < tolerance;
}

type Color = number[];

interface BaseObject {
    [key: string]: any;
}

interface Position {
    y: number;
    x: number;
    isBucket: boolean;
    newColor?: string;
    tolerance?: number;
    strokeColor?: string;
    moving?: boolean;
    lineWidth: number;
}

type ColorRGBA = {
    r: number;
    g: number;
    b: number;
    a?: number;
};
interface CanvasFreeDrawingParameters {
    canvas: HTMLCanvasElement;
    width: number;
    height: number;
    elementId?: string;
    backgroundColor?: string;
    lineWidth?: number;
    strokeColor?: string;
    disabled?: string;
    showWarnings?: boolean;
    maxSnapshots?: number;
    bucketToolTolerance?: number;
    bucketMode?: boolean;
}
interface NodeColorCache {
    [key: string]: boolean;
}

export enum AllowedEvents {
    redraw = 'redraw',
    fill = 'fill',
    mouseup = 'mouseup',
    mousedown = 'mousedown',
    mouseenter = 'mouseenter',
    mouseleave = 'mouseleave',
}
export default class CanvasFreeDrawing {
    elementId: string | void;
    canvasNode: HTMLElement | null;
    canvas: HTMLCanvasElement;
    context: CanvasRenderingContext2D;
    width: number;
    height: number;
    maxSnapshots: number;
    snapshots: ImageData[];
    undos: ImageData[];
    positions: [Position[]?];
    leftCanvasDrawing: boolean;
    isDrawing: boolean;
    isDrawingModeEnabled: boolean;
    imageRestored: boolean;
    lineWidth: number;
    backgroundColor?: string;
    strokeColor: string;
    bucketToolColor: string;
    bucketToolTolerance: number;
    public isBucketToolEnabled: boolean;
    listenersList: string[];
    redrawCounter: number;
    dispatchEventsOnceEvery: number;
    allowedEvents: string[];

    events: { [key: string]: Event };
    bindings: {
        mouseDown: (event: MouseEvent) => void;
        mouseMove: (event: MouseEvent) => void;
        mouseLeave: (event: MouseEvent) => void;
        mouseUp: (event: MouseEvent) => void;
        mouseUpDocument: (event: MouseEvent) => void;
        touchStart: (event: TouchEvent) => void;
        touchMove: (event: TouchEvent) => void;
        touchEnd: (event: MouseEvent) => void;
        [name: string]: unknown;
    };

    touchIdentifier?: number;
    previousX?: number;
    previousY?: number;
    showWarnings: boolean;

    constructor(params: CanvasFreeDrawingParameters) {
        const {
            canvas,
            width,
            height,
            backgroundColor = "#ffffff",
            lineWidth = 5,
            strokeColor = "#4d4d4d",
            disabled,
            showWarnings = false,
            maxSnapshots = 10,
            bucketToolTolerance = 10,
            bucketMode = true
        } = params;

        this.requiredParam(params, 'canvas');
        this.requiredParam(params, 'width');
        this.requiredParam(params, 'height');

        this.canvas = canvas;

        this.context = <CanvasRenderingContext2D>this.canvas.getContext('2d');

        this.width = width;
        this.height = height;

        this.maxSnapshots = maxSnapshots;
        this.snapshots = [];

        this.undos = [];
        this.positions = [];
        this.leftCanvasDrawing = false; // to check if user left the canvas drawing, on mouseover resume drawing
        this.isDrawing = false;
        this.isDrawingModeEnabled = true;
        this.imageRestored = false;

        this.lineWidth = lineWidth;
        this.strokeColor = strokeColor;
        this.bucketToolColor = strokeColor;
        this.bucketToolTolerance = bucketToolTolerance;
        this.isBucketToolEnabled = bucketMode;

        this.listenersList = [
            'mouseDown',
            'mouseMove',
            'mouseLeave',
            'mouseUp',
            'touchStart',
            'touchMove',
            'touchEnd',
            'dblclick'
        ];
        this.allowedEvents = this.getAllowedEvents();
        this.redrawCounter = 0;
        this.dispatchEventsOnceEvery = 0; // this may become something like: [{event, counter}]

        // // initialize events
        this.events = {
            redrawEvent: new Event('cfd_redraw'),
            fillEvent: new Event('cfd_fill'),
            mouseUpEvent: new Event('cfd_mouseup'),
            mouseDownEvent: new Event('cfd_mousedown'),
            mouseEnterEvent: new Event('cfd_mouseenter'),
            mouseLeaveEvent: new Event('cfd_mouseleave'),
            touchStartEvent: new Event('cfd_touchstart'),
            touchEndEvent: new Event('cfd_touchend'),
        };

        this.bindings = {
            mouseDown: this.mouseDown.bind(this),
            mouseMove: this.mouseMove.bind(this),
            mouseLeave: this.mouseLeave.bind(this),
            mouseUp: this.mouseUp.bind(this),
            mouseUpDocument: this.mouseUpDocument.bind(this),
            touchStart: this.touchStart.bind(this),
            touchMove: this.touchMove.bind(this),
            touchEnd: this.touchEnd.bind(this),
        };

        this.touchIdentifier = undefined;
        this.previousX = undefined;
        this.previousY = undefined;

        this.showWarnings = showWarnings;

        this.storeSnapshot();

        if (!disabled) this.enableDrawingMode();

        // clean up the image
        const imageData = this.context.getImageData(0, 0, width, height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            const pixel = {
                r: data[i],
                g: data[i + 1],
                b: data[i + 2],
                a: data[i + 3]
            }
            if (differenceCiede2000(pixel, "#ffffff") > this.bucketToolTolerance) {

            }
        }
        this.context.putImageData(imageData, 0, 0);
    }

    requiredParam(object: BaseObject, param: string): void {
        if (!object || !object[param]) {
            throw new Error(`${param} is required`);
        }
    }

    logWarning(...args: string[]): void {
        if (this.showWarnings) console.warn(...args);
    }

    addListeners(): void {
        this.listenersList.forEach((event) => {
            this.canvas.addEventListener(
                event.toLowerCase(),
                this.bindings[event] as EventListenerObject
            );
        });
        document.addEventListener('mouseup', this.bindings.mouseUpDocument);
    }

    removeListeners(): void {
        this.listenersList.forEach((event) => {
            this.canvas.removeEventListener(
                event.toLowerCase(),
                this.bindings[event] as EventListenerObject
            );
        });
        document.removeEventListener('mouseup', this.bindings.mouseUpDocument);
    }

    getAllowedEvents(): string[] {
        const events = [];
        for (const event in AllowedEvents) {
            events.push(event);
        }
        return events;
    }

    enableDrawingMode(): boolean {
        this.isDrawingModeEnabled = true;
        this.addListeners();
        this.toggleCursor();
        return this.isDrawingModeEnabled;
    }

    disableDrawingMode(): boolean {
        this.isDrawingModeEnabled = false;
        this.removeListeners();
        this.toggleCursor();
        return this.isDrawingModeEnabled;
    }

    mouseDown(event: MouseEvent): void {
        if (event.button !== 0) return;
        this.drawPoint(event.offsetX, event.offsetY);
    }

    mouseMove(event: MouseEvent): void {
        this.drawLine(event.offsetX, event.offsetY, event);
    }

    touchStart(event: TouchEvent): void {
        if (event.changedTouches.length > 0) {
          const { pageX, pageY, identifier } = event.changedTouches[0];
          const x = pageX - this.canvas.offsetLeft;
          const y = pageY - this.canvas.offsetTop;
          this.touchIdentifier = identifier;
          this.drawPoint(x, y);
        }
      }
    
      touchMove(event: TouchEvent): void {
        if (event.changedTouches.length > 0) {
          const { pageX, pageY, identifier } = event.changedTouches[0];
          const x = pageX - this.canvas.offsetLeft;
          const y = pageY - this.canvas.offsetTop;
    
          // check if is multi touch, if it is do nothing
          if (identifier != this.touchIdentifier) return;
    
          this.previousX = x;
          this.previousY = y;
          this.drawLine(x, y, event);
        }
      }

    touchEnd(): void {
        this.handleEndDrawing();
        this.canvas.dispatchEvent(this.events.touchEndEvent);
    }

    mouseUp(): void {
        this.handleEndDrawing();
        this.canvas.dispatchEvent(this.events.mouseUpEvent);
    }

    mouseUpDocument(): void {
        this.leftCanvasDrawing = false;
    }

    mouseLeave(): void {
        if (this.isDrawing) this.leftCanvasDrawing = true;
        this.isDrawing = false;
        this.canvas.dispatchEvent(this.events.mouseLeaveEvent);
    }

    mouseEnter(): void {
        this.canvas.dispatchEvent(this.events.mouseEnterEvent);
    }

    handleEndDrawing(): void {
        this.isDrawing = false;
        this.storeSnapshot();
    }

    drawPoint(x: number, y: number): void {
        if (this.isBucketToolEnabled) {
            try {
                const imageData = this.context.getImageData(0, 0, this.width, this.height);
                const floodFill = new FloodFill(imageData);
                floodFill.isSameColor = isSameColor
                floodFill.fill(this.bucketToolColor, x, y, this.bucketToolTolerance);
                this.context.putImageData(floodFill.imageData, 0, 0);
            } catch (e) {
                // just in case.
            }
        } else {
            this.isDrawing = true;
            this.storeDrawing(x, y, false);
            this.canvas.dispatchEvent(this.events.mouseDownEvent);
            this.handleDrawing();
        }
    }

    drawLine(x: number, y: number, event: MouseEvent | TouchEvent): void {
        if (this.leftCanvasDrawing) {
            this.leftCanvasDrawing = false;
            if (event instanceof MouseEvent) {
                this.mouseDown(event);
            } else if (event instanceof TouchEvent) {
                this.touchEnd();
            }
        }

        if (this.isDrawing) {
            this.storeDrawing(x, y, true);
            this.handleDrawing(this.dispatchEventsOnceEvery);
        }
    }

    handleDrawing(dispatchEventsOnceEvery?: number): void {
        this.context.lineJoin = 'round';
        const positions = [[...this.positions].pop()];

        positions.forEach((position) => {
            if (position && position[0] && position[0].strokeColor) {
                this.context.strokeStyle = position[0].strokeColor;
                this.context.lineWidth = position[0].lineWidth;
                this.draw(position);
            }
        });

        if (!dispatchEventsOnceEvery) {
            this.canvas.dispatchEvent(this.events.redrawEvent);
        } else if (this.redrawCounter % dispatchEventsOnceEvery === 0) {
            this.canvas.dispatchEvent(this.events.redrawEvent);
        }

        this.undos = [];
        this.redrawCounter += 1;
    }

    draw(position: Position[]): void {
        position.forEach(({ x, y, moving }, i) => {
            this.context.beginPath();
            if (moving && i) {
                this.context.moveTo(position[i - 1]['x'], position[i - 1]['y']);
            } else {
                this.context.moveTo(x - 1, y);
            }
            this.context.lineTo(x, y);
            this.context.closePath();
            this.context.stroke();
        });
    }



    toValidColor(color: Color): Color {
        if (Array.isArray(color) && color.length === 4) return color;
        if (Array.isArray(color) && color.length === 3) {
            const validColor = [...color];
            validColor.push(255);
            return validColor;
        } else {
            this.logWarning(
                'Color is not valid!\n' +
                'It must be an array with RGB values:  [0-255, 0-255, 0-255]'
            );
            return [0, 0, 0, 255];
        }
    }


    rgbaFromArray(a: Color): string {
        return `rgba(${a[0]},${a[1]},${a[2]},${a[3]})`;
    }

    setDimensions(): void {
        this.canvas.height = this.height;
        this.canvas.width = this.width;
    }

    toggleCursor(): void {
        this.canvas.style.cursor = this.isDrawingModeEnabled ? 'crosshair' : 'auto';
    }

    storeDrawing(x: number, y: number, moving: boolean): void {
        if (moving) {
            const lastIndex = this.positions.length - 1;
            (this.positions[lastIndex] as Position[]).push({
                x,
                y,
                moving,
                lineWidth: this.lineWidth,
                strokeColor: this.strokeColor,
                isBucket: false,
            });
        } else {
            this.positions.push([
                {
                    x,
                    y,
                    isBucket: false,
                    moving,
                    lineWidth: this.lineWidth,
                    strokeColor: this.strokeColor,
                },
            ]);
        }
    }

    storeSnapshot(): void {
        const imageData = this.getCanvasSnapshot();
        this.snapshots.push(imageData);
        if (this.snapshots.length > this.maxSnapshots) {
            this.snapshots = this.snapshots.splice(-Math.abs(this.maxSnapshots));
        }
    }

    getCanvasSnapshot(): ImageData {
        return this.context.getImageData(0, 0, this.width, this.height);
    }

    restoreCanvasSnapshot(imageData: ImageData): void {
        this.context.putImageData(imageData, 0, 0);
    }

    // Public APIs

    on(
        params: { event: AllowedEvents; counter?: number },
        callback: () => void
    ): void {
        const { event, counter } = params;
        this.requiredParam(params, 'event');

        if (this.allowedEvents.includes(event)) {
            if (event === 'redraw' && counter && Number.isInteger(counter)) {
                this.dispatchEventsOnceEvery = counter;
            }
            this.canvas.addEventListener('cfd_' + event, () => callback());
        } else {
            this.logWarning(`This event is not allowed: ${event}`);
        }
    }

    setLineWidth(px: number): void {
        this.lineWidth = px;
    }

    setBackground(color: string, save = true): void {
        const validColor = color;
        if (validColor) {
            if (save) this.backgroundColor = validColor;
            this.context.fillStyle = validColor;
            this.context.fillRect(0, 0, this.width, this.height);
        }
    }

    setDrawingColor(color: string): void {
        this.configBucketTool({ color });
        this.setStrokeColor(color);
    }
    getDrawingColor() {
        return this.strokeColor;
    }

    setStrokeColor(color: string): void {
        this.strokeColor = color; //default direct color
    }

    setBucketColor(color: string): void {
        this.configBucketTool({ color });
    }

    configBucketTool(params: { color?: string; tolerance?: number }): void {
        const { color = null, tolerance = null } = params;
        if (color) this.bucketToolColor = color;

        if (tolerance && tolerance > 0) {
            this.bucketToolTolerance = tolerance > 100 ? 100 : tolerance;
        }
    }

    toggleBucketTool(): boolean {
        return (this.isBucketToolEnabled = !this.isBucketToolEnabled);
    }

    setBucketMode(mode = true): void {
        this.isBucketToolEnabled = mode;
    }

    toggleDrawingMode(): boolean {
        return this.isDrawingModeEnabled
            ? this.disableDrawingMode()
            : this.enableDrawingMode();
    }

    clear(): void {
        this.context.clearRect(0, 0, this.width, this.height);
        this.positions = [];
        this.imageRestored = false;
        if (this.backgroundColor) this.setBackground(this.backgroundColor);
        this.handleEndDrawing();
    }

    save(): string {
        return this.canvas.toDataURL('image/jpg');
    }

    restore(image: HTMLImageElement, callback?: () => void): void {
        this.imageRestored = true;
        this.context.drawImage(image, 0, 0, this.width, this.height);
        if (typeof callback === 'function') callback();
    }

    undo(): void {
        const lastSnapshot = this.snapshots[this.snapshots.length - 1];
        const goToSnapshot = this.snapshots[this.snapshots.length - 2];
        if (goToSnapshot) {
            this.restoreCanvasSnapshot(goToSnapshot);
            this.snapshots.pop();
            this.undos.push(lastSnapshot);
            this.undos = this.undos.splice(-Math.abs(this.maxSnapshots));
            this.imageRestored = true;
        } else {
            this.logWarning('There are no more undos left.');
        }
    }

    redo(): void {
        if (this.undos.length > 0) {
            const lastUndo = this.undos.pop();
            if (lastUndo) {
                this.restoreCanvasSnapshot(lastUndo);
                this.snapshots.push(lastUndo);
                this.snapshots = this.snapshots.splice(-Math.abs(this.maxSnapshots));
            }
        } else {
            this.logWarning('There are no more redo left.');
        }
    }
}